import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { calculateIkrProgress } from '../../lib/utils';
import KPIProgressBar from '../../components/ui/KPIProgressBar';
import IndividualKrRow from './IndividualKrRow';

export default function KeyResultRow({ kr, onUpdate, onDeleteKr, users, teams, isLocked, viewer, selectedTeamId }) {
  const [krState, setKrState] = useState(kr);
  const [isIndividualKrsExpanded, setIndividualKrsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(kr.text === 'New Team Key Result');
  const [krText, setKrText] = useState(kr.text);
  const canEdit = viewer.role === 'Admin' || viewer.role === 'L1 Team Lead' || viewer.role === 'L2 Team Lead';

  const handleIKRUpdate = (ikrId, updatedIkr) => {
    const newIkrs = krState.individualKRs.map(ikr => (ikr.id === ikrId ? updatedIkr : ikr));
    const newState = { ...krState, individualKRs: newIkrs };
    setKrState(newState);
    onUpdate(newState);
  };

  const addIndividualKr = () => {
    const newIkr = { id: `ikr_${Date.now()}`, text: '', ownerId: null, teamId: null, kpiTarget: 0, kpiUnit: '', quarterlyActuals: [null, null, null, null], selfNotes: ['', '', '', ''], managerNotes: ['', '', '', ''] };
    const newIkrs = [...(krState.individualKRs || []), newIkr];
    const newState = { ...krState, individualKRs: newIkrs };
    setKrState(newState);
    onUpdate(newState);
    setIndividualKrsExpanded(true);
  };

  const deleteIndividualKr = (ikrId) => {
    const newIkrs = krState.individualKRs.filter(ikr => ikr.id !== ikrId);
    setKrState(prev => ({ ...prev, individualKRs: newIkrs }));
    onUpdate({ ...krState, individualKRs: newIkrs });
  };

  const individualKRs = krState.individualKRs || [];
  const overallProgress = individualKRs.length > 0
    ? individualKRs.reduce((sum, ikr) => sum + calculateIkrProgress(ikr), 0) / individualKRs.length
    : 0;

  const handleUpdate = (field, value) => {
    const newState = { ...krState, [field]: value };
    setKrState(newState);
    onUpdate(newState);
  };

  const saveKrText = () => {
    setIsEditing(false);
    handleUpdate('text', krText);
  };

  return (
    <div className="py-3 border-b border-gray-200 last:border-b-0">
      <div className="grid grid-cols-12 gap-4 items-center">
        <div className="col-span-1 flex items-center">
          <button onClick={() => setIndividualKrsExpanded(!isIndividualKrsExpanded)} className="text-gray-500 hover:text-blue-600 p-1 rounded-full hover:bg-gray-100">
            {isIndividualKrsExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>
        <div className="col-span-4">
          {isEditing ? (
            <input type="text" value={krText} onChange={(e) => setKrText(e.target.value)} onBlur={saveKrText} onKeyPress={e => { if (e.key === 'Enter') saveKrText(); }} className="w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500" autoFocus disabled={!canEdit} />
          ) : (
            <p className={`text-gray-800 font-medium ${canEdit ? 'cursor-pointer hover:text-blue-600' : ''}`} onClick={() => canEdit && setIsEditing(true)}>{krState.text}</p>
          )}
        </div>
        <div className="col-span-2">
          <select value={krState.teamId || ''} onChange={(e) => handleUpdate('teamId', e.target.value)} className="w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500" disabled={!canEdit}>
            <option value="" disabled>Assign Team</option>
            {Object.entries(teams).map(([id, teamData]) => <option key={id} value={id}>{teamData.name}</option>)}
          </select>
        </div>
        <div className="col-span-2 grid grid-cols-2 gap-2">
          <input type="number" value={krState.kpiTarget || ''} placeholder="Target" onChange={(e) => handleUpdate('kpiTarget', parseFloat(e.target.value) || 0)} className="w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500" disabled={!canEdit} />
          <input type="text" value={krState.kpiUnit} placeholder="Unit" onChange={(e) => handleUpdate('kpiUnit', e.target.value)} className="w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500" disabled={!canEdit} />
        </div>
        <div className="col-span-2 flex items-center">
          <div className="w-full"><KPIProgressBar value={overallProgress} /></div>
          <span className="text-sm font-semibold ml-2 w-10 text-right">{(overallProgress * 100).toFixed(0)}%</span>
        </div>
        <div className="col-span-1 flex justify-end">
          {viewer.role === 'Admin' && (
            <button onClick={() => onDeleteKr(kr.id)} className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-colors">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
      {isIndividualKrsExpanded && (
        <div className="mt-3 pl-4 space-y-2 overflow-x-auto">
          <div style={{ width: '1005px' }}>
            <div className="flex gap-2 items-center mb-1 text-xs font-semibold text-gray-500 pl-1">
              <div style={{ width: '220px' }}>Individual Key Result</div>
              <div style={{ width: '120px' }}>Owner</div>
              <div style={{ width: '100px' }}>Team</div>
              <div style={{ width: '55px' }}>Target</div>
              <div style={{ width: '60px' }}>Unit</div>
              <div style={{ width: '65px' }}>Method</div>
              <div style={{ width: '240px' }} className="text-center">Quarterly Actuals</div>
              <div style={{ width: '100px' }}>Progress</div>
              <div style={{ width: '30px' }} />
            </div>
            {(krState.individualKRs || []).map(ikr => (
              <IndividualKrRow key={ikr.id} ikr={ikr} onUpdate={handleIKRUpdate} onDelete={deleteIndividualKr} users={users} teams={teams} isLocked={!canEdit} viewer={viewer} selectedTeamId={selectedTeamId} />
            ))}
            <div className="pt-2 pl-1">
              <button onClick={addIndividualKr} className="flex items-center text-xs text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400 disabled:cursor-not-allowed" disabled={!canEdit}>
                <Plus size={14} className="mr-1" /> Add Individual KR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
